import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Alert,
    CircularProgress,
} from '@mui/material';
import { QrCodeScanner as QrCodeScannerIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import QRCodeScanner from '../components/QRCodeScanner';
import { useFeatureConfig } from '../hooks/useFeatureConfig';
import { getConfiguration } from '../services/coreService';
import { memberService } from '../services/memberService';
import apiClient from '../config/api';

const MemberSignup = () => {
    const navigate = useNavigate();
    const { qrCodeSupported, loaded } = useFeatureConfig();
    const [membershipNumber, setMembershipNumber] = useState('');
    const [qrScannerOpen, setQrScannerOpen] = useState(false);
    const [autoMembershipNumberEnabled, setAutoMembershipNumberEnabled] = useState(false);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [isSigningOut, setIsSigningOut] = useState(false);

    // Check AutoMembershipNumber config on component mount
    useEffect(() => {
        const checkAutoMembershipNumberConfig = async () => {
            try {
                setLoadingConfig(true);
                // Application ID = 2, Config Key = "AutoMembershipNumber"
                const configs = await getConfiguration(2, 'AutoMembershipNumber');
                
                if (configs && configs.length > 0) {
                    const configValue = configs[0].ConfigValue;
                    const isEnabled = configValue === '1';
                    setAutoMembershipNumberEnabled(isEnabled);
                    
                    // If enabled, fetch and set the next membership number
                    if (isEnabled) {
                        try {
                            const response = await memberService.getNextMembershipNumber();
                            if (response && response.nextMembershipNumber) {
                                setMembershipNumber(response.nextMembershipNumber);
                            }
                        } catch (error) {
                            console.error('Error fetching next membership number:', error);
                        }
                    }
                } else {
                    // If config doesn't exist, default to false (manual entry)
                    setAutoMembershipNumberEnabled(false);
                }
            } catch (error) {
                console.error('Error checking AutoMembershipNumber config:', error);
                // On error, default to false (manual entry)
                setAutoMembershipNumberEnabled(false);
            } finally {
                setLoadingConfig(false);
            }
        };

        checkAutoMembershipNumberConfig();
    }, []);

    const handleQrScanSuccess = (qrData) => {
        // Only allow QR scan if auto membership number is disabled
        if (!autoMembershipNumberEnabled) {
            setMembershipNumber(qrData);
        }
    };

    const handleOpenQrScanner = () => {
        // Only allow QR scanner if auto membership number is disabled
        if (!autoMembershipNumberEnabled) {
            setQrScannerOpen(true);
        }
    };

    const handleCloseQrScanner = () => {
        setQrScannerOpen(false);
    };

    const handleMemberSignup = async () => {
        // Sign out before navigating to member form
        try {
            setIsSigningOut(true);
            // Call logout API directly to clear session
            try {
                await apiClient.post('/api/auth/logout');
            } catch (error) {
                // Even if logout fails, continue to navigation
                console.error('Error logging out:', error);
            }
            // Navigate to MemberForm with membership number
            navigate('/member-form', { 
                state: { 
                    membershipNumber
                } 
            });
        } catch (error) {
            console.error('Error during signup:', error);
            setIsSigningOut(false);
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddIcon color="primary" />
                Member Signup
            </Typography>

            <Paper sx={{ p: 3, mt: 3 }}>
                {/* Membership Card Number Input */}
                {loadingConfig ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TextField
                        fullWidth
                        label="Membership card #"
                        value={membershipNumber}
                        onChange={(e) => {
                            // Only allow changes if auto membership number is disabled
                            if (!autoMembershipNumberEnabled) {
                                setMembershipNumber(e.target.value);
                            }
                        }}
                        placeholder={autoMembershipNumberEnabled ? "Membership number will be auto-generated" : "Enter or scan membership card number"}
                        InputProps={{
                            readOnly: autoMembershipNumberEnabled,
                            endAdornment: (loaded && qrCodeSupported && !autoMembershipNumberEnabled) ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleOpenQrScanner}
                                        edge="end"
                                        color="primary"
                                        aria-label="scan QR code"
                                    >
                                        <QrCodeScannerIcon />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        sx={{ mb: 3 }}
                    />
                )}

                {/* Information Alert */}
                <Alert severity="info" sx={{ mb: 3 }}>
                    Click the "Member Signup" button below will bring you to the Customer Signup Form. 
                    Please note that after the customer finishes filling the form, the app will not navigate back to this site.
                </Alert>

                {/* Member Signup Button */}
                <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={!membershipNumber.trim() || isSigningOut}
                    onClick={handleMemberSignup}
                    sx={{ py: 1.5 }}
                >
                    {isSigningOut ? 'Signing out...' : 'Member Signup'}
                </Button>
            </Paper>

            {/* QR Code Scanner Dialog - Only show if auto membership number is disabled */}
            {loaded && qrCodeSupported && !autoMembershipNumberEnabled && (
                <QRCodeScanner
                    open={qrScannerOpen}
                    onClose={handleCloseQrScanner}
                    onScanSuccess={handleQrScanSuccess}
                />
            )}
        </Box>
    );
};

export default MemberSignup;

